import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { dateDiffInSeconds } from '../../../components/Timer/utils';
import { getErrorMessage } from '../../../utils/errors';
import { formatTime } from '../../../utils/time';

interface UseCountdownTimerProps {
  endDate: Date | string;
  onEnd: () => void;
}

const clampSeconds = (value: number): number => (value > 0 ? value : 0);

export function useCountdownTimer(props: UseCountdownTimerProps) {
  const { endDate, onEnd } = props;
  const [seconds, setSeconds] = useState(() => {
    const diff = dateDiffInSeconds(new Date(endDate));
    return Number.isFinite(diff) ? clampSeconds(diff) : 0;
  });
  const onTimerEndRef = useRef(onEnd);
  const { t } = useTranslation();
  const isExpired = seconds <= 0;
  const timer = formatTime(Math.max(seconds, 0), 's', t);

  useEffect(() => {
    onTimerEndRef.current = onEnd;
  }, [onEnd]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    try {
      if (!endDate) return;

      interval = setInterval(() => {
        const next = dateDiffInSeconds(new Date(endDate));
        if (!Number.isFinite(next)) {
          setSeconds(0);
          clearInterval(interval!);
          return;
        }
        if (next <= 0) {
          setSeconds(0);
          onTimerEndRef.current?.();
          clearInterval(interval!);
          return;
        }
        setSeconds(clampSeconds(next));
      }, 1000);
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      console.error(`Timer error: ${errorMessage}`);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [endDate]);

  return { timer, isExpired };
}
